import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import useScrollReveal from "../../hooks/useScrollReveal";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import { PackageCard } from "../site/OfferingPrimitives";
import MonthlySupport from "../site/MonthlySupport";
import {
  WEBSITE_PACKAGES,
  LANDING_PAGE_PACKAGE,
  toPricingCardShape,
} from "../../data/websitePackages";

const openAudit = (offer) =>
  window.dispatchEvent(
    new CustomEvent("open-audit", { detail: { offer } }),
  );

/* ---------- Pricing Data ---------- */

const websitePackages = WEBSITE_PACKAGES.map(toPricingCardShape);
const landingPagePackage = toPricingCardShape(LANDING_PAGE_PACKAGE);

const revenueAudit = {
  id: "pricing-revenue-audit",
  title: "Revenue Leak Audit",
  price: "$497",
  priceLabel: "One-time · Credited toward implementation",
  description:
    "A diagnostic that maps where your business is losing leads, bookings, and revenue — across your website, forms, follow-up, booking flow, and CRM.",
  builtFor: [
    "Businesses already getting leads",
    "Operators who suspect a leak but can't pinpoint it",
  ],
  includes: [
    "Full lead-to-cash flow review",
    "Where leads drop off (and why)",
    "Mapped fix priorities",
    "Clear next-step recommendation",
    "Credit applied if you build within 7 days",
  ],
  cta: "Start Revenue Audit",
};

const revenueImplementation = [
  {
    id: "pricing-lead-capture",
    title: "Lead Capture System",
    price: "$2,000",
    priceLabel: "One-time",
    description:
      "A clean intake system so leads from forms, ads, DMs, calls, and referrals never fall through the cracks.",
    builtFor: ["Businesses with leaky intake", "Operators ready to centralize lead flow"],
    includes: [
      "Lead intake form build",
      "CRM connection",
      "Routing + notifications",
      "Source tracking",
      "Basic reporting",
    ],
    cta: "Build My Lead Capture System",
  },
  {
    id: "pricing-follow-up",
    title: "Follow-Up + Booking System",
    price: "$4,250",
    priceLabel: "One-time",
    badge: "Best for service businesses",
    description:
      "Lead capture plus the CRM, booking flow, and follow-up automation that turns inbound interest into booked calls.",
    builtFor: [
      "Service businesses losing booked calls",
      "Owners drowning in manual follow-up",
    ],
    includes: [
      "Everything in Lead Capture System",
      "CRM setup + workflows",
      "Booking flow + reminders",
      "Multi-step follow-up automation",
      "Internal notification rules",
      "Lead source attribution",
    ],
    cta: "Build My Follow-Up System",
    emphasized: true,
  },
  {
    id: "pricing-revenue-conversion",
    title: "Revenue Conversion System",
    price: "$10,000",
    priceLabel: "One-time",
    description:
      "The full lead-to-cash build — landing page, CRM, follow-up, payment + booking flow, tracking, reactivation, review collection, and reporting.",
    builtFor: [
      "Businesses ready to operationalize revenue",
      "Owners who want one system handling the entire flow",
    ],
    includes: [
      "Everything in Follow-Up + Booking",
      "Conversion landing page",
      "Payment / booking flow",
      "Reactivation campaign",
      "Review collection flow",
      "Full reporting dashboard",
    ],
    cta: "Build My Revenue System",
  },
];

const fullBuildout = {
  id: "pricing-full-buildout",
  title: "Full Revenue System Buildout",
  price: "Custom Quote",
  priceLabel: "Application required",
  description:
    "A flagship custom buildout for established operators who want a fully-installed lead-to-cash system tailored to their business model and tools.",
  builtFor: [
    "Established service businesses",
    "Operators with complex tool stacks",
    "Teams scaling beyond manual workflows",
  ],
  includes: [
    "Custom revenue system architecture",
    "Multi-tool integration",
    "Custom CRM workflows",
    "Custom reporting + dashboards",
    "Onboarding + training",
    "Priority support window",
  ],
  cta: "Apply for Full Buildout",
};

/* ---------- Page ---------- */

export default function PricingPage() {
  useScrollReveal();
  const navigate = useNavigate();

  const onWebsiteCta = (pkg) => {
    // Map the pricing card's test-id to the brief's internal package key.
    // Starter/Growth/Premium all deep-link and preselect Step 2; Landing Page
    // (when added) would go straight to the brief without preselection.
    const map = {
      "pricing-starter-website": "starter",
      "pricing-growth-website": "growth",
      "pricing-premium-website": "premium",
    };
    const pkgKey = map[pkg.id];
    navigate(pkgKey ? `/build-brief?pkg=${pkgKey}` : "/build-brief");
  };

  const onRevenueCta = (pkg) => openAudit(pkg.title);

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="pricing-page">
      <SEO page="/pricing" />
      <Nav />
      <main>
        <Hero />
        <WebsitesPricing onCta={onWebsiteCta} />
        <RevenuePricing
          onAuditCta={() => openAudit("Revenue Leak Audit")}
          onImplCta={onRevenueCta}
          onBuildoutCta={() => openAudit("Full Revenue System Buildout")}
        />
        <MonthlySupport tone="dark" />
        <FinalCta onCta={() => openAudit("Revenue Leak Audit")} />
      </main>
      <Footer />
    </div>
  );
}

/* ---------- Sections ---------- */

function Hero() {
  return (
    <section
      data-testid="pricing-hero"
      className="pt-24 sm:pt-28 md:pt-32 pb-14 sm:pb-16 md:pb-20 px-5 sm:px-6 md:px-10"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7 reveal">
            <p className="eyebrow !text-clay">Pricing</p>
            <h1
              data-kinetic
              data-testid="pricing-headline"
              className="font-display mt-5 text-[2.2rem] sm:text-[2.8rem] md:text-[3.4rem] lg:text-[4rem] leading-[1.04] tracking-[-0.02em] font-semibold text-ink"
            >
              Two paths.{" "}
              <span className="italic text-ink-muted font-normal">Clear pricing.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-base md:text-lg text-ink-muted leading-relaxed">
              Website packages launch fast and look credible. Revenue System packages fix the lead-to-cash flow behind the website. Pick the path that matches the leak.
            </p>
            <a
              href="#websites-pricing"
              data-testid="pricing-jump-websites"
              className="mt-6 inline-flex items-center gap-2 text-[14px] text-ink hover:text-clay transition-colors underline underline-offset-4"
            >
              Jump to Websites <ArrowRight size={13} />
            </a>
            <span className="mx-3 text-ink-muted/60">·</span>
            <a
              href="#revenue-pricing"
              data-testid="pricing-jump-revenue"
              className="inline-flex items-center gap-2 text-[14px] text-ink hover:text-clay transition-colors underline underline-offset-4"
            >
              Jump to Revenue Systems <ArrowRight size={13} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function WebsitesPricing({ onCta }) {
  return (
    <section
      id="websites-pricing"
      data-testid="pricing-websites"
      className="scroll-mt-24 sm:scroll-mt-28 py-16 sm:py-20 md:py-24 px-5 sm:px-6 md:px-10 border-t border-sand-300"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-10 sm:mb-12 reveal">
          <div className="lg:col-span-7">
            <p className="eyebrow">Modern Exit Websites</p>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-[2.6rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
              I need a credible website that converts.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-ink-muted leading-relaxed">
              Productized website tiers for businesses that need a clean, modern, conversion-ready website before adding a full revenue system.
            </p>
          </div>
        </div>

        {/* Add-on · Campaign Page — sits ABOVE the 3-card main row */}
        <div className="reveal text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
            Add-on · Campaign Page
          </p>
        </div>
        <div className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <div className="hidden lg:block" aria-hidden="true" />
          <PackageCard pkg={landingPagePackage} onCta={onCta} tone="light" />
          <div className="hidden lg:block" aria-hidden="true" />
        </div>

        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {websitePackages.map((p) => (
            <PackageCard key={p.id} pkg={p} onCta={onCta} tone="light" />
          ))}
        </div>
      </div>
    </section>
  );
}

function RevenuePricing({ onAuditCta, onImplCta, onBuildoutCta }) {
  return (
    <section
      id="revenue-pricing"
      data-testid="pricing-revenue"
      className="scroll-mt-24 sm:scroll-mt-28 bg-[#0F0F0E] text-[#F4F0E8] py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-10 sm:mb-12 reveal">
          <div className="lg:col-span-7">
            <p className="eyebrow !text-[#A56A43]">Modern Exit Revenue Systems</p>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-[2.6rem] leading-[1.05] tracking-[-0.02em] font-semibold text-[#F4F0E8]">
              I need my leads to{" "}
              <span className="italic text-[#BEB8AF] font-normal">turn into revenue.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-[#BEB8AF] leading-relaxed">
              Start with the $497 Revenue Audit. Move into a full system build only when the leak is mapped.
            </p>
          </div>
        </div>

        {/* Audit row — full width emphasis */}
        <div className="reveal">
          <article
            data-testid="pricing-audit-card"
            className="rounded-[32px] border border-[#3C3935] bg-[#151514] p-7 sm:p-9 md:p-10 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start"
          >
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] bg-[#A56A43] text-[#F4F0E8]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F4F0E8]" />
                Start here
              </span>
              <h3 className="font-display mt-5 text-2xl sm:text-[1.8rem] md:text-[2rem] font-semibold tracking-tight text-[#F4F0E8]">
                {revenueAudit.title}
              </h3>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <span className="font-display text-[2rem] sm:text-[2.4rem] font-semibold tracking-[-0.02em] leading-none text-[#F4F0E8]">
                  {revenueAudit.price}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8E887F]">
                  {revenueAudit.priceLabel}
                </span>
              </div>
              <p className="mt-5 text-[15px] leading-relaxed text-[#BEB8AF] max-w-2xl">
                {revenueAudit.description}
              </p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {revenueAudit.includes.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-[#F4F0E8]"
                  >
                    <span className="mt-0.5 h-4 w-4 grid place-items-center rounded-full bg-[#B1744B]/15 text-[#B1744B] shrink-0">
                      <Check size={10} strokeWidth={2.4} />
                    </span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 lg:pl-6 lg:border-l lg:border-[#33302D]">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A56A43]">
                Built for
              </p>
              <ul className="mt-3 space-y-1.5 text-[13.5px] leading-relaxed text-[#BEB8AF]">
                {revenueAudit.builtFor.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="text-[#8E887F]">·</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={onAuditCta}
                data-testid="pricing-audit-cta"
                className="mt-7 group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#A56A43] hover:bg-[#B1744B] text-[#F4F0E8] px-5 py-3.5 text-[14px] font-medium transition-colors min-h-[48px]"
              >
                {revenueAudit.cta}
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </button>
              <p className="mt-3 text-[12px] text-[#8E887F] leading-relaxed">
                Move into a system build within 7 days and the $497 is credited.
              </p>
            </div>
          </article>
        </div>

        {/* Implementation tiers */}
        <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {revenueImplementation.map((p) => (
            <PackageCard key={p.id} pkg={p} onCta={onImplCta} tone="dark" />
          ))}
        </div>

        {/* Full custom flagship */}
        <div className="mt-10 sm:mt-12 reveal">
          <article
            data-testid="pricing-full-buildout-card"
            className="rounded-[32px] border border-[#3C3935] bg-[#1A1A18] p-7 sm:p-9 md:p-10 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start"
          >
            <div className="lg:col-span-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A56A43]">
                Custom flagship
              </p>
              <h3 className="font-display mt-4 text-2xl sm:text-[1.8rem] md:text-[2rem] font-semibold tracking-tight text-[#F4F0E8]">
                {fullBuildout.title}
              </h3>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <span className="font-display text-[1.8rem] sm:text-[2rem] font-semibold tracking-[-0.02em] leading-none text-[#F4F0E8]">
                  {fullBuildout.price}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8E887F]">
                  {fullBuildout.priceLabel}
                </span>
              </div>
              <p className="mt-5 text-[15px] leading-relaxed text-[#BEB8AF] max-w-2xl">
                {fullBuildout.description}
              </p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {fullBuildout.includes.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-[#F4F0E8]"
                  >
                    <span className="mt-0.5 h-4 w-4 grid place-items-center rounded-full bg-[#B1744B]/15 text-[#B1744B] shrink-0">
                      <Check size={10} strokeWidth={2.4} />
                    </span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-4 lg:pl-6 lg:border-l lg:border-[#33302D]">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8E887F]">
                Built for
              </p>
              <ul className="mt-3 space-y-1.5 text-[13.5px] leading-relaxed text-[#BEB8AF]">
                {fullBuildout.builtFor.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="text-[#8E887F]">·</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={onBuildoutCta}
                data-testid="pricing-buildout-cta"
                className="mt-7 group inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#3C3935] hover:border-[#A56A43] hover:bg-[#1A1A18] text-[#F4F0E8] px-5 py-3.5 text-[14px] font-medium transition-colors min-h-[48px]"
              >
                {fullBuildout.cta}
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </button>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onCta }) {
  return (
    <section
      data-testid="pricing-final-cta"
      className="py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10 border-t border-sand-300"
    >
      <div className="max-w-3xl mx-auto text-center reveal">
        <p className="eyebrow">Not sure which build fits?</p>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
          Start with the $497 Revenue Audit.
        </h2>
        <p className="mt-6 text-base md:text-lg text-ink-muted leading-relaxed max-w-xl mx-auto">
          We'll map the leak, recommend the right path, and credit the audit toward your build if you move within 7 days.
        </p>
        <div className="mt-10">
          <button
            type="button"
            onClick={onCta}
            data-testid="pricing-final-cta-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-rust transition-colors min-h-[52px]"
          >
            Start Revenue Audit
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>
      </div>
    </section>
  );
}
